/**
 * Exportación a Excel (.xlsx) del reporte Costos/m² (ProductividadPantalla).
 *
 * Genérico sobre la definición de columnas del reporte: recibe las columnas
 * VISIBLES en el orden elegido por el usuario y el árbol Proyecto→Bloque→Obra ya
 * aplanado (con subtotales + Gran Total). Escribe VALORES NUMÉRICOS CRUDOS +
 * numFmt según el tipo de cada columna (moneda ₡, número, %, índice), y texto
 * para columnas de texto (Estado). Las celdas "—" de la vista quedan en blanco.
 *
 * exceljs se importa DINÁMICAMENTE para no cargar la librería en el bundle
 * inicial; solo se descarga al exportar.
 */

export type ExcelFmt = 'moneda' | 'numero' | 'porcentaje' | 'indice' | 'texto';

const NUM_FMT: Record<Exclude<ExcelFmt, 'texto'>, string> = {
  moneda: '"₡"#,##0',
  numero: '#,##0',
  porcentaje: '0.0%',
  indice: '#,##0.00',
};

export interface ColumnaCostosM2Export {
  etiqueta: string;
  fmt: ExcelFmt;
}

export interface CeldaCostosM2Export {
  /** number para columnas numéricas (% en fracción 0-1), string para texto, null = celda vacía. */
  valor: number | string | null;
}

export interface FilaCostosM2Export {
  /** 0 = proyecto, 1 = bloque, 2 = obra. */
  depth: number;
  etiqueta: string;
  esSubtotal: boolean;
  esGranTotal?: boolean;
  /** Alineadas 1:1 con `columnas`. */
  celdas: CeldaCostosM2Export[];
}

export interface OpcionesCostosM2Export {
  nombreArchivo: string;
  tituloHoja: string;
  encabezadoJerarquia: string;
  columnas: ColumnaCostosM2Export[];
  filas: FilaCostosM2Export[];
}

function argbSubtotal(depth: number): string {
  if (depth === 0) return 'FFD1FAE5'; // emerald-100
  if (depth === 1) return 'FFECFDF5'; // emerald-50
  return 'FFF7FEE7'; // lime-50
}
const ARGB_GRAN_TOTAL = 'FFE5E7EB';
const ARGB_HEADER = 'FF1F6B3B';

export async function exportarCostosM2Excel(opts: OpcionesCostosM2Export): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Adelante BI Obras';
  wb.created = new Date();
  const ws = wb.addWorksheet(opts.tituloHoja, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }],
  });

  const totalCols = opts.columnas.length + 1;

  // --- Encabezado (fila 1) ---
  ws.getCell(1, 1).value = opts.encabezadoJerarquia;
  opts.columnas.forEach((c, i) => {
    ws.getCell(1, i + 2).value = c.etiqueta;
  });
  for (let c = 1; c <= totalCols; c++) {
    const cell = ws.getCell(1, c);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB_HEADER } };
    cell.alignment = { horizontal: c === 1 ? 'left' : 'right', vertical: 'middle' };
  }

  // --- Filas de datos ---
  for (const fila of opts.filas) {
    const fileRow = ws.addRow([]);
    const sangria = '    '.repeat(fila.depth);
    const celdaJerarquia = fileRow.getCell(1);
    celdaJerarquia.value = `${sangria}${fila.etiqueta}`;
    if (fila.esSubtotal || fila.esGranTotal) celdaJerarquia.font = { bold: true };

    const fondo = fila.esGranTotal
      ? ARGB_GRAN_TOTAL
      : fila.esSubtotal
        ? argbSubtotal(fila.depth)
        : null;
    if (fondo) {
      celdaJerarquia.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fondo } };
    }

    opts.columnas.forEach((col, i) => {
      const cell = fileRow.getCell(i + 2);
      const dato = fila.celdas[i];
      if (dato && dato.valor !== null && dato.valor !== undefined) {
        cell.value = dato.valor;
        if (col.fmt !== 'texto' && typeof dato.valor === 'number') cell.numFmt = NUM_FMT[col.fmt];
      }
      cell.alignment = { horizontal: col.fmt === 'texto' ? 'left' : 'right' };
      if (fondo) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fondo } };
      }
    });
  }

  // Anchos.
  ws.getColumn(1).width = 40;
  for (let c = 2; c <= totalCols; c++) ws.getColumn(c).width = 16;

  // Descargar.
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.nombreArchivo.endsWith('.xlsx')
    ? opts.nombreArchivo
    : `${opts.nombreArchivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
